import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

export const MAX_INTERIOR_BOXES = 16;
export const INTERIOR_LIMITS = {
  position: { x: [-4, 4], y: [-2, 4], z: [-6, 6] },
  size: { x: [.01, 8], y: [.01, 6], z: [.01, 12] },
  rotationDegrees: { x: [-180, 180], y: [-180, 180], z: [-180, 180] },
};

export function createInteriorBox(id = THREE.MathUtils.generateUUID()) {
  return {
    id, name: 'Caja interior', enabled: true,
    position: { x: 0, y: .35, z: 0 },
    size: { x: 1.4, y: .25, z: 3 },
    rotationDegrees: { x: 0, y: 0, z: 0 },
    color: '#202126',
  };
}

export function sanitizeVehicleInteriors(source) {
  const usedIds = new Set();
  const boxes = (Array.isArray(source?.boxes) ? source.boxes : []).slice(0, MAX_INTERIOR_BOXES).map((entry, index) => {
    const input = entry || {};
    const fallback = createInteriorBox(`interior-${index + 1}`);
    let id = String(input.id || fallback.id).slice(0, 100);
    while (usedIds.has(id)) id += '-copy';
    usedIds.add(id);
    const box = {
      id, name: String(input.name || fallback.name).slice(0, 80),
      enabled: input.enabled !== false,
      color: /^#[0-9a-f]{6}$/i.test(input.color) ? input.color : fallback.color,
    };
    for (const [property, axes] of Object.entries(INTERIOR_LIMITS)) {
      box[property] = {};
      for (const [axis, [min, max]] of Object.entries(axes)) {
        const raw = input[property]?.[axis];
        const value = raw == null || raw === '' ? NaN : Number(raw);
        box[property][axis] = THREE.MathUtils.clamp(Number.isFinite(value) ? value : fallback[property][axis], min, max) || 0;
      }
    }
    return box;
  });
  return { enabled: source?.enabled !== false, boxes };
}

export function duplicateInteriorBox(box, mirror = false) {
  const copy = structuredClone(box);
  copy.id = THREE.MathUtils.generateUUID();
  copy.name = `${box.name} ${mirror ? 'espejada' : 'copia'}`.slice(0, 80);
  if (mirror) {
    copy.position.x = -copy.position.x || 0;
    copy.rotationDegrees.y = -copy.rotationDegrees.y || 0;
    copy.rotationDegrees.z = -copy.rotationDegrees.z || 0;
  }
  return copy;
}

export function interiorBoxMatrix(box) {
  const rotation = new THREE.Euler(...['x', 'y', 'z'].map((axis) => THREE.MathUtils.degToRad(box.rotationDegrees[axis])));
  return new THREE.Matrix4().compose(
    new THREE.Vector3(box.position.x, box.position.y, box.position.z),
    new THREE.Quaternion().setFromEuler(rotation),
    new THREE.Vector3(box.size.x, box.size.y, box.size.z),
  );
}

// One mesh/material per vehicle, even when individual boxes have different colors.
export function createInteriorGeometry(boxes) {
  const geometries = boxes.filter((box) => box.enabled).map((box) => {
    const geometry = new THREE.BoxGeometry(1, 1, 1);
    geometry.applyMatrix4(interiorBoxMatrix(box));
    const color = new THREE.Color(box.color);
    const colors = new Float32Array(geometry.attributes.position.count * 3);
    for (let i = 0; i < colors.length; i += 3) color.toArray(colors, i);
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    return geometry;
  });
  if (!geometries.length) return null;
  const geometry = mergeGeometries(geometries, false);
  geometries.forEach((part) => part.dispose());
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  return geometry;
}
